// src/pages/VirtualConsultationPage.jsx
import React from "react";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";

const VirtualConsultationPage = () => {
  const navigate = useNavigate();

  const services = [
    "Video consultations with certified doctors",
    "Secure online platform for private communication",
    "Medical advice, prescriptions, and follow-ups",
    "Flexible scheduling at your convenience",
  ];

  const steps = [
    {
      title: "Step 1",
      description: "Schedule a convenient time for your virtual consultation.",
      color: "#204080",
    },
    {
      title: "Step 2",
      description:
        "Join a secure video call with our certified doctor at the scheduled time.",
      color: "#2E5AAC",
    },
    {
      title: "Step 3",
      description:
        "Receive professional advice, prescriptions, and follow-up guidance.",
      color: "#204080",
    },
  ];

  const handleBookAppointment = () => {
    navigate("/book-appointment?type=virtual-consultation");
  };

  return (
    <>
      <Header hideBookButton={true} />

      {/* Hero Section */}
   <section className="relative w-full h-screen min-h-[600px] bg-gradient-to-b from-blue-100 to-white overflow-hidden pt-20"> {/* pt-20 added */}
  <img
    src="/virtualConsult.jpg"
    alt="Virtual Consultation"
    className="absolute inset-0 w-full h-full object-cover brightness-75 saturate-110"
    loading="lazy"
  />
  {/* <div className="absolute inset-0 bg-gradient-to-r from-[#204080]/90 to-transparent"></div> */}

  <div className="relative flex flex-col justify-center h-full px-6 md:px-12 lg:px-20 text-white z-10">
    <div className="max-w-2xl text-left">
      <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold leading-tight drop-shadow-lg mb-6">
        Expert <span className="text-[#204080]">Care</span> <br /> Right at Your <span className="text-[#204080]">
        Fingertips</span>
      </h1>
      <p className="text-lg md:text-xl mb-10 font-light leading-relaxed drop-shadow-md">
        Connect with top healthcare providers from wherever you are. Our virtual consultation service provides expert care with convenience, privacy, and safety — eliminating travel and wait times while keeping you in control of your health.
      </p>

      <button
        onClick={() => navigate("/book-appointment?type=virtual")}
        className="bg-[#204080] hover:bg-[#2E5AAC] transition shadow-xl rounded-full px-10 py-4 font-semibold text-lg text-white hover:shadow-2xl transform hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-blue-300"
        aria-label="Book a Virtual Consultation"
      >
        Book a Virtual Consultation
      </button>
    </div>
  </div>
</section>


      {/* How We Treat Virtually */}
      <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-white py-16">
        <section className="py-16 px-4 max-w-5xl mx-auto  ">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#204080]">
            How Our Virtual Consultation Works
          </h2>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <p className="text-gray-700 text-lg leading-relaxed">
                Connect with healthcare professionals from the comfort of your
                home. We provide timely and effective consultations online with
                full privacy.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {services.map((service, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 bg-white shadow-lg p-4 rounded-xl hover:shadow-2xl transition"
                  >
                    <CheckCircle className="text-[#204080] w-6 h-6 mt-1 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{service}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() =>
                  navigate("/book-appointment", {
                    state: { consultationType: "Virtual Consultation" },
                  })
                }
                className="mt-4 px-8 py-3 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Book Virtual Consultation
              </button>
            </div>
            <div>
              <img
                src="./vc.jpg"
                alt="Home Visit Care"
                className="rounded-xl shadow-lg"
              />
            </div>
          </div>
        </section>
      </div>

      {/* Steps Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#204080] mb-12">
            What to Expect During a Virtual Consultation
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="bg-white shadow-lg rounded-xl p-6 hover:shadow-2xl transition flex flex-col items-center text-center"
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-4"
                  style={{ backgroundColor: step.color }}
                >
                  {idx + 1}
                </div>
                <h3 className="text-xl font-semibold text-[#204080] mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-700">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-br from-slate-50 via-blue-50 to-white overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 text-center text-[#204080]">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4">
            Ready to Book Your Virtual Consultation?
          </h2>
          <p className="text-lg mb-6">
            Take the first step towards convenient, professional care from your
            home.
          </p>
          <button
            onClick={() => navigate("/book-appointment?type=virtual")}
            className="px-8 py-3 bg-[#204080] text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            Book Now
          </button>
        </div>
      </section>
    </>
  );
};

export default VirtualConsultationPage;
